import Api from './axiosInstance'
import { API_PRICING } from 'utils/constants'

type FetchPricingParams = {
  limit: number
  page: number
  search?: string
  filters?: any
  barcodeToken?: string | null
  barcode?: string | null
}

// To fetch all pricing data
const fetchPricings = ({
  limit,
  page,
  search,
  filters,
  barcodeToken,
  barcode,
}: FetchPricingParams) => {
  let options: any = {}

  if (search) {
    options.search = search
  }

  if (filters) {
    options = { ...options, ...filters }
  }

  const params = new URLSearchParams({
    limit,
    page,
    ...options,
  })

  // Advance search by barcode: scope the list to the uploaded barcode set
  if (barcodeToken) {
    params.set('barcodeToken', barcodeToken)
  }

  // Advance search by barcode: typed/pasted exact barcodes (comma-separated)
  if (barcode) {
    params.set('barcode', String(barcode))
  }

  return Api.get({
    url: `${API_PRICING.PRICING}?${params}`,
  })
}

// To fetch individual pricing pricing data
const getPricingDetails = (id?: string) => {
  return Api.get({
    url: `${API_PRICING.PRICING}/${id}`,
  })
}

// To update individual pricing pricing data
const updatePricingDetails = (id: string, data: Record<string, any>) => {
  return Api.patch({
    url: `${API_PRICING.PRICING}/${id}`,
    data,
  })
}

// To Upload a Pricing file in server and update Pricing data
const importPricingData = async (data: any) => {
  return Api.post({
    url: API_PRICING.PRICING_IMPORT,
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// To Upload a Pricing file in server and update Pricing data
const importOnlinePricingData = async (data: any) => {
  return Api.post({
    url: API_PRICING.ONLINE_PRICING_IMPORT,
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// To Upload a Pricing file in server and update Pricing data
const importAggregatorWiseOnlinePricingData = async (data: any) => {
  return Api.post({
    url: API_PRICING.AGGREGATOR_ONLINE_PRICING_IMPORT,
    data: data,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// To Upload a Pricing file in server and update Pricing data
const exportPricingData = ({
  filters,
  barcodeToken,
  barcode,
}: {
  filters: Record<string, any>
  barcodeToken?: string | null
  barcode?: string | null
}) => {
  const params = new URLSearchParams(filters)

  // Advance search by barcode: scope the export to the uploaded barcode set
  if (barcodeToken) {
    params.set('barcodeToken', barcodeToken)
  }

  // Advance search by barcode: typed/pasted exact barcodes (comma-separated)
  if (barcode) {
    params.set('barcode', String(barcode))
  }

  return Api.get({
    url: `${API_PRICING.PRICING_EXPORT}?${params}`,
  })
}

// Advance search by barcode: upload a single-column CSV of barcodes and get a
// short-lived token (valid 60 min) that scopes the pricing list/export.
const uploadPricingBarcodeSearchFile = (file: File) => {
  const formData = new FormData()
  // field name MUST be "file"
  formData.append('file', file)

  return Api.post({
    url: API_PRICING.PRICING_BARCODE_SEARCH,
    data: formData,
    headers: { 'Content-Type': 'multipart/form-data' },
  })
}

// To Upload a Pricing file in server and update Pricing data
const generateInvalidOnlinePricing = ({
  filters,
}: {
  filters: Record<string, any>
}) => {
  const params = new URLSearchParams(filters)
  return Api.post({
    url: `${API_PRICING.INVALID_PRICE_REPORT}?${params}`,
  })
}

const downloadUpdatedErpPriceReport = ({
  filters,
}: {
  filters: Record<string, any>
}) => {
  const params = new URLSearchParams(filters)
  return Api.post({
    url: `${API_PRICING.UPDATED_ERP_PRICE_REPORT}?${params}`,
  })
}

export {
  fetchPricings,
  getPricingDetails,
  updatePricingDetails,
  importPricingData,
  exportPricingData,
  importOnlinePricingData,
  generateInvalidOnlinePricing,
  downloadUpdatedErpPriceReport,
  importAggregatorWiseOnlinePricingData,
  uploadPricingBarcodeSearchFile,
}
